import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["loader", "list", "empty", "saveBtn", "ongoingNotice"]
  static values = {
    fetchUrl: String,
    saveUrl: String,
    canEdit: { type: Boolean, default: false }
  }

  connect() {
    this._fetched = false
    document.addEventListener("modal:open-by-id", this._onModalOpened = (e) => {
      if (e.detail?.id === "attendance-sheet" && !this._fetched) {
        this._fetch()
      }
    })
    document.addEventListener("click", this._onTriggerClick = (e) => {
      const trigger = e.target.closest('[data-modal-open="attendance-sheet"]')
      if (trigger && !this._fetched) {
        this._fetch()
      }
    })
  }

  disconnect() {
    document.removeEventListener("modal:open-by-id", this._onModalOpened)
    document.removeEventListener("click", this._onTriggerClick)
  }

  refresh(event) {
    if (event) event.preventDefault()
    this._fetched = false
    this._fetch()
  }

  addRow(event) {
    if (event) event.preventDefault()
    this.listTarget.appendChild(this._buildRow(""))
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
    this._refreshSaveBtnState()
  }

  removeRow(event) {
    if (event) event.preventDefault()
    const row = event.target.closest("[data-attendance-row]")
    if (row) row.remove()
    if (!this.listTarget.querySelector("[data-attendance-row]") && this.hasEmptyTarget) {
      this.emptyTarget.classList.remove("hidden")
    }
  }

  // Download PDF saves first (BHD 2026-08-11). The export renders from
  // the server's copy, so anything typed-but-unsaved — or a live roster
  // nobody pressed Save on — would silently vanish from the PDF.
  async downloadPdf(event) {
    if (event) event.preventDefault()
    const url = event?.currentTarget?.dataset?.pdfUrl
    if (!url) return

    if (!this.canEditValue || this._currentNames().length === 0) {
      window.open(url, "_blank")
      return
    }

    // The tab has to be opened inside the click itself — opening it after
    // the save resolves puts it outside the user gesture and popup
    // blockers kill it.
    const tab = window.open("", "_blank")
    const saved = await this._persist({ closeOnSuccess: false, silent: true })
    if (!saved) {
      tab?.close() // the toast already explained; never export a stale sheet
      return
    }
    if (tab) tab.location = url
    else window.open(url, "_blank")
  }

  async save(event) {
    if (event) event.preventDefault()
    await this._persist({ closeOnSuccess: true, silent: false })
  }

  _currentNames() {
    return Array.from(this.listTarget.querySelectorAll("input[data-attendance-name]"))
      .map((input) => input.value.trim())
      .filter((name) => name.length > 0)
  }

  async _persist({ closeOnSuccess = true, silent = false } = {}) {
    if (!this.canEditValue) return false

    const names = this._currentNames()

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this.saveBtnTarget.disabled = true
    const originalText = this.saveBtnTarget.textContent
    this.saveBtnTarget.textContent = "Saving..."

    try {
      const formData = new FormData()
      names.forEach((name) => formData.append("data[]", name))

      const response = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken },
        body: formData
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      await response.json()

      if (!silent) this._showToast("Attendance sheet saved", "notice")
      if (closeOnSuccess) {
        this.element.classList.add("hidden")
        document.body.style.overflow = ""
      }
      return true
    } catch (err) {
      this._showToast("Could not save attendance sheet. Please try again.", "alert")
      return false
    } finally {
      this.saveBtnTarget.disabled = false
      this.saveBtnTarget.textContent = originalText
    }
  }

  async _fetch() {
    this._fetched = true
    this._showLoader(true)
    this._clearList()

    try {
      const response = await fetch(this.fetchUrlValue, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()
      this._render(data)
    } catch (err) {
      this._showToast("Could not load attendance sheet.", "alert")
    } finally {
      this._showLoader(false)
    }
  }

  _render(data) {
    let names = []
    if (Array.isArray(data.attendance_sheet)) {
      names = data.attendance_sheet
    } else if (Array.isArray(data.participants_unique)) {
      names = data.participants_unique.map((p) => p.name).filter(Boolean)
    }

    if (this.hasOngoingNoticeTarget) {
      this.ongoingNoticeTarget.classList.toggle("hidden", !data.is_ongoing)
    }

    if (names.length === 0) {
      if (this.hasEmptyTarget) this.emptyTarget.classList.remove("hidden")
    } else {
      if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
      names.forEach((name) => this.listTarget.appendChild(this._buildRow(name)))
    }

    this._refreshSaveBtnState()
  }

  _buildRow(value) {
    const row = document.createElement("div")
    row.dataset.attendanceRow = ""
    row.className = "flex items-center gap-2"

    const input = document.createElement("input")
    input.type = "text"
    input.dataset.attendanceName = ""
    input.value = value
    input.placeholder = "Attendee name"
    input.disabled = !this.canEditValue
    input.className = "flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand disabled:bg-gray-50 disabled:text-gray-500"
    row.appendChild(input)

    if (this.canEditValue) {
      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.dataset.action = "click->attendance-sheet#removeRow"
      removeBtn.className = "text-red-500 hover:text-red-700 p-1 cursor-pointer"
      removeBtn.title = "Remove"
      removeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>`
      row.appendChild(removeBtn)
    }

    return row
  }

  _clearList() {
    this.listTarget.innerHTML = ""
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
  }

  _showLoader(show) {
    if (this.hasLoaderTarget) this.loaderTarget.classList.toggle("hidden", !show)
  }

  _refreshSaveBtnState() {
    if (!this.hasSaveBtnTarget) return
    this.saveBtnTarget.classList.toggle("hidden", !this.canEditValue)
  }

  _showToast(message, type = "notice") {
    const container = document.createElement("div")
    container.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    container.dataset.controller = "flash"
    container.dataset.flashDismissAfterValue = "5000"
    container.setAttribute("role", "alert")

    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = type === "alert"
      ? `<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>`
      : `<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`

    const msgEl = document.createElement("p")
    msgEl.className = "flex-1 text-sm"
    msgEl.textContent = message

    container.appendChild(iconDiv)
    container.appendChild(msgEl)
    document.body.insertBefore(container, document.body.firstChild)
  }
}
